import { useCallback, useEffect, useRef, useState } from 'react';
import { isNil } from 'lodash';
// Import Types
import type { ActiveStepActions, SaveButtonOption, StepperRef, UseStepNativeActionsOptions } from './types';
import type { StepperHeadlineData } from './StepperPageHeadline/StepperPageHeadline';

export const useStepNativeActions = (options: UseStepNativeActionsOptions) => {
    const {
        formik,
        defaultValues = {},
        canClear,
        canReset,
        setStepValues,
        updateStepConfig,
        stepperSubmitCount,
        onReset,
        onClear,
        onApplyValues,
        customIsValid,
        customErrorMessage,
        customIsDirty,
    } = options;
    const {
        values = {},
        initialValues = {},
        isValid,
        isValidating,
        dirty,
        setValues,
        submitForm,
        submitCount,
    } = formik ?? {};

    const [loadPrecessesCount, setPendingPrecessesCount] = useState(0);
    const [isFormInitialized, setIsFormInitialized] = useState(false);
    const [isPendingSubmit, setIsPendingSubmit] = useState(false);
    const isLoading = !!loadPrecessesCount;

    const handleClearData = useCallback(async () => {
        await setValues!({ ...defaultValues, editData: initialValues.editData }, true);
        onClear?.();
        submitForm!();
    }, [defaultValues, initialValues.editData, onClear, setValues, submitForm]);

    const handleResetData = useCallback(async () => {
        await setValues!(initialValues, true);
        onReset?.();
        submitForm!();
    }, [initialValues, onReset, setValues, submitForm]);

    // Applies externally supplied values (e.g. a restored draft) on top of the initial data.
    // Formik `initialValues` stay untouched, so Reset keeps returning to the initial data.
    const handleApplyValues = useCallback(
        (newValues: Record<string, unknown>) => {
            if (onApplyValues) {
                onApplyValues(newValues);
                return;
            }
            setValues!({ ...initialValues, ...newValues }, true);
        },
        [initialValues, onApplyValues, setValues],
    );

    useEffect(() => {
        if (!formik) {
            return;
        }

        updateStepConfig({
            submit: () => {
                setIsPendingSubmit(true);
            },
            applyValues: handleApplyValues,
            ...(canClear ? { clear: handleClearData } : {}),
            ...(canReset ? { reset: handleResetData } : {}),
        });
    }, [canClear, canReset, formik, handleApplyValues, handleClearData, handleResetData, updateStepConfig]);

    useEffect(() => {
        if (!formik) {
            return;
        }

        updateStepConfig({ isDirty: customIsDirty ?? !!dirty });
    }, [customIsDirty, dirty, formik, updateStepConfig]);

    const isStepSubmitted = submitCount !== 0;

    useEffect(() => {
        if (!submitForm || isStepSubmitted || isNil(stepperSubmitCount) || stepperSubmitCount === 0) {
            return;
        }

        submitForm();
    }, [stepperSubmitCount, submitForm, isStepSubmitted]);

    useEffect(() => {
        if (isPendingSubmit && !isLoading) {
            submitForm?.();

            setIsPendingSubmit(false);
        }
    }, [isPendingSubmit, isLoading, submitForm]);

    useEffect(() => {
        setStepValues?.(values);
    }, [setStepValues, values]);

    useEffect(() => {
        if (isNil(customIsValid)) {
            if (isFormInitialized && !isValidating) {
                updateStepConfig({
                    isValid: isValid ?? null,
                });
            }
        } else {
            updateStepConfig({
                isValid: customIsValid,
                ...(customErrorMessage ? { errorMessage: customErrorMessage } : {}),
            });
        }
    }, [customErrorMessage, customIsValid, isFormInitialized, isValid, isValidating, updateStepConfig]);

    const increaseLoadProcessesCount = useCallback(() => {
        setPendingPrecessesCount((prev) => ++prev);
    }, []);

    const decreaseLoadProcessesCount = useCallback(() => {
        setPendingPrecessesCount((prev) => {
            if (prev === 0) return prev;
            return --prev;
        });
    }, []);

    useEffect(() => {
        updateStepConfig({ isValidating, isLoading });
    }, [isLoading, isValidating, updateStepConfig]);

    // Form initially isValid === true, this is to keep it null until first validation
    useEffect(() => {
        if (!isFormInitialized && isValidating) {
            setIsFormInitialized(true);
        }
    }, [isFormInitialized, isValidating]);

    return {
        isLoading,
        increaseLoadProcessesCount,
        decreaseLoadProcessesCount,
        handleClearData,
        handleResetData,
    };
};

export const useStepperControls = () => {
    const stepperRef = useRef<StepperRef>(null);
    const headlineDataRef = useRef<StepperHeadlineData>({ name: '', description: '' });

    const [isStepperSubmitting, setIsStepperSubmitting] = useState(false);
    const [activeStepActions, setActiveStepActions] = useState<ActiveStepActions>({ hasClear: false, hasReset: false });

    const handlePageHeadlineSave = useCallback((saveOption: SaveButtonOption, headlineData: StepperHeadlineData) => {
        headlineDataRef.current = headlineData;
        stepperRef.current?.submit(saveOption);
    }, []);

    const handleActiveStepActionsChange = useCallback((actions: ActiveStepActions) => {
        setActiveStepActions(actions);
    }, []);

    const handleClear = activeStepActions.hasClear ? () => stepperRef.current?.clear?.() : undefined;
    const handleReset = activeStepActions.hasReset ? () => stepperRef.current?.reset?.() : undefined;

    return {
        stepperRef,
        headlineDataRef,
        isStepperSubmitting,
        setIsStepperSubmitting,
        handlePageHeadlineSave,
        handleActiveStepActionsChange,
        handleClear,
        handleReset,
    };
};
